/**
 * Separator Component
 *
 * A visual divider component for separating content sections.
 * Supports horizontal and vertical orientations.
 */

import React from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// TYPES
// =============================================================================

export interface SeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Orientation of the separator
   */
  orientation?: 'horizontal' | 'vertical';

  /**
   * Whether the separator is decorative (no semantic meaning)
   */
  decorative?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Separator component for visual content division
 */
export const Separator = React.forwardRef<HTMLDivElement, SeparatorProps>(
  (
    {
      className,
      orientation = 'horizontal',
      decorative = true,
      ...props
    },
    ref
  ) => {
    const separatorClassName = cn(
      'shrink-0 bg-[var(--border-default)]',
      orientation === 'horizontal'
        ? 'h-[1px] w-full'
        : 'h-full w-[1px]',
      className
    );

    return (
      <div
        ref={ref}
        role={decorative ? 'none' : 'separator'}
        aria-orientation={decorative ? undefined : orientation}
        className={separatorClassName}
        {...props}
      />
    );
  }
);

Separator.displayName = 'Separator';

// =============================================================================
// EXPORTS
// =============================================================================

export default Separator;
